<script setup lang="ts">
import type { ISchoolGradeDisplayConfiguration } from '@/core/school-grades/schoolGrade.types';
import { immutableSort } from 'common/utils/array.utils';
import { computed, nextTick, ref, watch, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocale } from '@/composables/useLocale';
import { formatNumber } from '@/utils/string.utils';
import type { ISchoolGradeInputProps } from './schoolGradeInput.types';
import SchoolGradeDecimalInput from './SchoolGradeDecimalInput.vue';
import SchoolGradeModifierPopover from './SchoolGradeModifierPopover.vue';
import SchoolGradeOption from './SchoolGradeOption.vue';
import RatingPopover from '@/core/elements/RatingPopover.vue';
import { useMobile } from '@/composables/useMobile';
import { getCompactPerformanceLevelGroupWidth } from '@/core/performance-visuals/performanceLevelRating.utils';
import {
  getSchoolGradePresentation,
  isSchoolGradeScaleReady,
  isValidSchoolGrade,
} from 'common/utils/school.grade.utils';
import { getSchoolGradeName } from './schoolGradePresentation.utils';
import { useSchoolGradeValue } from './useSchoolGradeValue';
import { useSchoolGradeEditor } from './useSchoolGradeEditor';
const props = defineProps<ISchoolGradeInputProps & { configuration: ISchoolGradeDisplayConfiguration }>();
const emit = defineEmits<{ 'update:modelValue': [value: number | null] }>();
const gradeLevels = computed(() => props.configuration.gradeLevels);
const lowerGradeValueIsBetter = computed(() => props.configuration.lowerGradeValueIsBetter);
const schoolGradeReady = computed(() => isSchoolGradeScaleReady(gradeLevels.value));
const { t } = useI18n();
const { formattingLocale } = useLocale();
const config = computed(() => props.configuration);
const { isMobile } = useMobile();
const usePopover = computed(() => isMobile.value && (!props.mode || props.mode === 'responsive'));
const ratingPopover = useTemplateRef<InstanceType<typeof RatingPopover>>('ratingPopover');
const ratingTrigger = ref<HTMLButtonElement | null>(null);
const ratingOpen = ref(false);
const canEdit = computed(() => !props.disabled && schoolGradeReady.value);
const gradeModel = useSchoolGradeValue(props, (next) => emit('update:modelValue', next));
const { failedSave } = gradeModel;
const {
  displayValue: value,
  collapsed,
  animateScale,
  editing,
  draft,
  modifierOpen,
  notation,
  selectedBase,
  commit,
  choose,
  acceptsDraft,
  updateDraft,
  commitDraft: commitDecimalDraft,
  cancelDraft,
  modifierDisabled,
  applyModifier,
} = useSchoolGradeEditor({ value: gradeModel.value, config, canEdit, save: gradeModel.save });
const scaleCollapsed = computed(() => !usePopover.value && collapsed.value);
const useCards = computed(() => props.mode === 'inline-cards');
// Both states size cards from the full control width, independently of the grade label.
const cardWidth = computed(() => {
  const count = Math.max(1, gradeLevels.value.length);
  return `min(100px, calc((100cqw - ${count - 1} * var(--grade-card-gap)) / ${count}))`;
});
const modifierAvailable = computed(
  () =>
    value.value != null &&
    !editing.value &&
    (config.value.decimalGradesEnabled || (config.value.gradeModifiersEnabled && !!notation.value)),
);
const mobileOption = useTemplateRef<InstanceType<typeof SchoolGradeOption>>('mobileOption');
const gradeOptions = useTemplateRef<InstanceType<typeof SchoolGradeOption>[]>('gradeOptions');
const modifierTrigger = computed(() => {
  if (usePopover.value) return mobileOption.value?.modifierButton ?? null;
  return gradeOptions.value?.find((option) => option.modifierButton)?.modifierButton ?? null;
});
const scale = useTemplateRef<HTMLElement>('scale');
const focusScaleGrade = (): void => {
  (
    scale.value?.querySelector<HTMLButtonElement>('button[aria-pressed="true"]') ??
    scale.value?.querySelector<HTMLButtonElement>('button')
  )?.focus();
};
const restoreGradeFocus = async (): Promise<void> => {
  await nextTick();
  if (usePopover.value) {
    ratingTrigger.value?.focus();
    return;
  }
  focusScaleGrade();
};
const commitWithKeyboard = (): void => {
  commitDraft();
  if (!editing.value) void restoreGradeFocus();
};
const cancelWithKeyboard = (): void => {
  cancelDraft();
  if (usePopover.value) hideRatingPopover();
  void restoreGradeFocus();
};
const presentation = computed(() => getSchoolGradePresentation(value.value, config.value, formattingLocale.value));
const selectedOptionPresentation = computed(() => {
  if (!scaleCollapsed.value && value.value != null && !isValidSchoolGrade(value.value, config.value))
    return getSchoolGradePresentation(selectedBase.value, config.value, formattingLocale.value);
  return presentation.value;
});
const gradeName = computed(() => (value.value == null ? '' : getSchoolGradeName(value.value, gradeLevels.value, t)));
const orderedGrades = computed(() =>
  immutableSort(gradeLevels.value, (a, b) =>
    lowerGradeValueIsBetter.value ? b.level - a.level : a.level - b.level,
  ).map((grade) => ({
    ...grade,
    presentation:
      grade.level === selectedBase.value
        ? selectedOptionPresentation.value
        : getSchoolGradePresentation(grade.level, config.value, formattingLocale.value),
  })),
);
const hideRatingPopover = (): void => {
  ratingPopover.value?.hide();
};
const handleRatingHide = (): void => {
  if (!ratingOpen.value) return;
  const restoreFocus = scale.value?.contains(document.activeElement);
  ratingOpen.value = false;
  if (editing.value) cancelDraft();
  if (restoreFocus) void restoreGradeFocus();
};
const showRatingPopover = async (event: MouseEvent): Promise<void> => {
  if (!canEdit.value) return;
  if (ratingOpen.value) {
    hideRatingPopover();
    return;
  }
  modifierOpen.value = false;
  ratingTrigger.value = event.currentTarget as HTMLButtonElement;
  await nextTick();
  await ratingPopover.value?.show();
  ratingOpen.value = true;
  focusScaleGrade();
};
const chooseGrade = (grade: number): void => {
  if (usePopover.value) {
    collapsed.value = config.value.decimalGradesEnabled && grade === selectedBase.value;
  }
  choose(grade);
  if (usePopover.value && !editing.value) {
    hideRatingPopover();
    void restoreGradeFocus();
  }
};
const commitDraft = (): void => {
  commitDecimalDraft();
  if (usePopover.value && !editing.value) hideRatingPopover();
};
const toggleModifier = (): void => {
  modifierOpen.value = !modifierOpen.value;
};
watch([usePopover, canEdit], () => {
  if (ratingOpen.value) hideRatingPopover();
});
const handleModifier = async (modifier: -1 | 1): Promise<void> => {
  applyModifier(modifier);
  if (modifierOpen.value) return;
  await restoreGradeFocus();
  modifierTrigger.value?.focus();
};
watch(editing, (active) => {
  if (!active) return;
  draft.value = formatNumber(Number(draft.value), { maximumFractionDigits: 2 });
});
</script>

<template>
  <div
    class="flex min-w-0 flex-col items-end gap-xs"
    :class="{ 'w-full [container-type:inline-size] [--grade-card-gap:theme(spacing.smd)]': useCards }"
    :style="useCards ? { '--grade-card-width': cardWidth } : undefined"
  >
    <div class="flex max-w-full items-center justify-end" :class="{ 'w-full': useCards }">
      <span
        class="min-w-0 truncate text-body font-semibold text-primary motion-school-grade-collapse"
        :class="[
          (usePopover || collapsed) && value != null ? 'mr-smd max-w-[288px] opacity-100' : 'max-w-0 opacity-0',
          { '!transition-none': !animateScale },
        ]"
        :aria-hidden="!usePopover && !collapsed"
        :title="gradeName"
        >{{ gradeName }}</span
      >
      <SchoolGradeOption
        v-if="usePopover"
        ref="mobileOption"
        :presentation="presentation"
        :selected="value != null"
        :disabled="!canEdit"
        :aria-label="`${accessibleLabel} ${presentation.label}`.trim()"
        aria-haspopup="dialog"
        :aria-expanded="ratingOpen"
        :modifier-available="modifierAvailable"
        :modifier-open="modifierOpen"
        @select="showRatingPopover"
        @modifier="toggleModifier"
      />
      <component
        :is="usePopover ? RatingPopover : 'div'"
        ref="ratingPopover"
        :class="{
          'shrink-0 motion-school-grade-collapse': useCards,
          'w-full': useCards && !scaleCollapsed,
          'w-[var(--grade-card-width)]': useCards && scaleCollapsed,
          '!transition-none': !animateScale,
        }"
        v-bind="usePopover ? { anchorElement: ratingTrigger, accessibleLabel } : {}"
        @hide="handleRatingHide"
      >
        <div
          ref="scale"
          class="flex max-w-full items-center"
          :class="{ 'gap-sm': usePopover, 'w-full justify-center': useCards, 'gap-smd': useCards && !scaleCollapsed }"
          :style="usePopover ? { width: `${getCompactPerformanceLevelGroupWidth(orderedGrades.length)}px` } : undefined"
          role="group"
          :aria-label="accessibleLabel"
        >
          <div
            v-for="grade in orderedGrades"
            :key="grade.level"
            class="motion-school-grade-collapse"
            :class="{
              'min-w-0 flex-1': usePopover,
              'shrink-0': !usePopover,
              'w-[var(--grade-card-width)]': useCards && (!scaleCollapsed || grade.level === selectedBase),
              'w-icon-xl': !usePopover && !useCards && (!scaleCollapsed || grade.level === selectedBase),
              'ml-sm first:ml-0': !usePopover && !useCards && !scaleCollapsed,
              'pointer-events-none m-0 w-0 overflow-hidden opacity-0': scaleCollapsed && grade.level !== selectedBase,
              '!transition-none': !animateScale,
            }"
          >
            <SchoolGradeOption
              ref="gradeOptions"
              :presentation="grade.presentation"
              :selected="grade.level === selectedBase"
              :disabled="!canEdit"
              :dimmed="value != null && grade.level !== selectedBase"
              :fluid="usePopover || useCards"
              :disable-transitions="!animateScale"
              :input-length="editing && grade.level === selectedBase ? draft.length : undefined"
              :tabindex="scaleCollapsed && grade.level !== selectedBase ? -1 : 0"
              :aria-hidden="scaleCollapsed && grade.level !== selectedBase ? true : undefined"
              :modifier-available="!usePopover && grade.level === selectedBase && modifierAvailable"
              :modifier-open="modifierOpen"
              @select="chooseGrade(grade.level)"
              @modifier="toggleModifier"
            >
              <template v-if="editing && grade.level === selectedBase" #default="{ chipClasses }">
                <SchoolGradeDecimalInput
                  :model-value="draft"
                  :class="chipClasses"
                  :accessible-label="accessibleLabel"
                  :accepts-draft="acceptsDraft"
                  @update:model-value="updateDraft"
                  @commit="commitDraft"
                  @commit:keyboard="commitWithKeyboard"
                  @cancel="cancelWithKeyboard"
                />
              </template>
            </SchoolGradeOption>
          </div>
        </div>
      </component>
    </div>
    <SchoolGradeModifierPopover
      v-model:open="modifierOpen"
      :anchor-element="modifierTrigger"
      :decimal-mode="config.decimalGradesEnabled"
      :modifier="presentation.modifier"
      :minus-disabled="modifierDisabled(-1)"
      :plus-disabled="modifierDisabled(1)"
      @select="handleModifier"
    />
    <p v-if="!schoolGradeReady" class="text-footer text-secondary">{{ t('schoolGrades.setupRequired') }}</p>
    <p v-if="failedSave && !editing" role="alert" class="text-footer text-error">
      {{ t('schoolGrades.saveFailed') }}
      <button type="button" class="underline" @click="commit(value)">
        {{ t('schoolGrades.retry') }}
      </button>
    </p>
  </div>
</template>
